import { LoginPaths } from '@hub/domains/login/route.paths';
import { trackBeforeNav } from '@platform/tracking';
import { redirect } from '@tanstack/react-router';
import type { RoutePaths } from '@tanstack/router-core';
import { HomePaths } from '../domains/home/route.paths';
import { publicHref } from './publicHref';
import { RouterPaths } from './routePaths';
import type { routeTree } from './routeTree';

type AppRoutePath = RoutePaths<typeof routeTree>;

export const DEFAULT_AUTHENTICATED_PATH: AppRoutePath = HomePaths.home.path;

export const FORBIDDEN_PATH: AppRoutePath = RouterPaths.forbidden.path;

export const LOGIN_ENTRY_POINT = '/identity/login';

// Current in-app path (pathname + query), the default post-login return target.
export const currentRelativePath = () => window.location.pathname + window.location.search;

// Path only, so the tracked `target` carries no query values.
const pathnameOf = (path: string) => path.replace(/\?.*$/, '');

export function buildLoginRedirectURL(redirectTo?: string, loginHint?: string): string {
  const target = redirectTo ?? currentRelativePath();
  const hint = loginHint ? `&login_hint=${encodeURIComponent(loginHint)}` : '';
  return `${LOGIN_ENTRY_POINT}?redirect=${encodeURIComponent(target)}${hint}`;
}

// `login_attempted`, not `login_started`: the flow is already entered.
// The return target defaults to home, not the current path: this runs on the login page, which
// would return to itself and loop. `target` leaves the app as a raw URL that the BFF hands back to
// the browser, so the default goes through `publicHref` — the locale and mount ride on it.
export const goToEmailLogin = (target?: string, loginHint?: string) => {
  trackBeforeNav('login_attempted', { method: 'email' });
  window.location.replace(buildLoginRedirectURL(target ?? publicHref(DEFAULT_AUTHENTICATED_PATH), loginHint));
};

// Imperative login navigation. Lives in the router layer (not the auth domain) so the
// authenticated guard and app shell can trigger login without a router→auth cycle.
// Fires only login_started: entering the flow is not a method choice. login_attempted
// is fired when the user actually picks a method (email button / SSO buttons).
export const login = ({ reason }: { reason?: 'session_expired' } = {}) => {
  const sessionExpired = reason === 'session_expired';
  trackBeforeNav('login_started', {
    source: sessionExpired ? 'session_expired' : 'explicit',
    target: window.location.pathname,
  });
  const url = buildLoginRedirectURL();
  // Session-expired: replace, so Back can't bounce onto the stale page and re-trigger the 401 loop.
  // Explicit login: push, so Back from the (302-only, non-persisted) login endpoint returns to the page left behind.
  if (sessionExpired) {
    window.location.replace(url);
  } else {
    window.location.assign(url);
  }
};

/**
 * Where an anonymous visitor lands. `page-register` says the identifier-first journey's pages are
 * reachable, which is not the same as guards sending everyone there: moving the entry is its own
 * change, not a flag flip. One seam, so that change has one place to happen.
 */
export const anonymousEntry = () => LoginPaths.login;

// The `to` is carried through the login and handed back to the browser, so it travels as a public
// href; the tracked target stays as the caller spelled it, which keeps it locale-independent.
export const loginRedirect = ({ redirectTo }: { redirectTo?: string } = {}) => {
  const target = redirectTo ?? currentRelativePath();
  trackBeforeNav('login_started', { source: 'guard', target: pathnameOf(target) });
  return redirect({ to: anonymousEntry().path, search: { to: publicHref(target) }, replace: true });
};

// Session-probe failure (non-401): auth state is unknown, so route to the error page
// (rendered in the app shell) instead of login. `retryUrl` carries the current path so the
// error page's "try again" does a full reload — a client-side nav would reuse the cached
// probe error and bounce straight back here. WWP-534.
export const errorRedirect = () => redirect({ to: RouterPaths.error.path, search: { retryUrl: currentRelativePath() } });
